import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  TrendingUp, 
  Phone, 
  Target, 
  Award,
  BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    try {
      const response = await fetch(`${API}/reports/rep-performance`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setPerformance(data);
      }
    } catch (error) {
      console.error("Failed to fetch performance:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalLeads = performance.reduce((sum, rep) => sum + rep.total_leads, 0);
  const totalClosedWon = performance.reduce((sum, rep) => sum + rep.closed_won, 0);
  const totalCalls = performance.reduce((sum, rep) => sum + rep.total_calls, 0);
  const avgConversion = performance.length > 0
    ? (performance.reduce((sum, rep) => sum + rep.conversion_rate, 0) / performance.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Reports
        </h1>
        <p className="text-slate-500 mt-1">
          Team performance and sales metrics
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <Card className="metric-card" data-testid="stat-total-assigned">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Total Assigned</p>
              <p className="metric-value">{totalLeads}</p>
            </div>
            <div className="p-2 bg-primary/10 rounded-md">
              <Target className="w-5 h-5 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="stat-total-won">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Deals Won</p>
              <p className="metric-value">{totalClosedWon}</p>
            </div>
            <div className="p-2 bg-emerald-50 rounded-md">
              <Award className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="stat-total-calls">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Total Calls</p>
              <p className="metric-value">{totalCalls}</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-md">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="stat-avg-conversion">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-label">Avg Conversion</p>
              <p className="metric-value">{avgConversion}%</p>
            </div>
            <div className="p-2 bg-indigo-50 rounded-md">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Performance table */}
      <Card data-testid="performance-table">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Rep Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {performance.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">No performance data yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Assign leads to team members to see their performance
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4">Rep</th>
                    <th className="text-center py-3 px-4">Total Leads</th>
                    <th className="text-center py-3 px-4">Closed Won</th>
                    <th className="text-center py-3 px-4">Closed Lost</th>
                    <th className="text-center py-3 px-4">Conversion</th>
                    <th className="text-center py-3 px-4">Total Calls</th>
                    <th className="text-center py-3 px-4">Calls This Week</th>
                  </tr>
                </thead>
                <tbody>
                  {performance
                    .sort((a, b) => b.conversion_rate - a.conversion_rate)
                    .map((rep, index) => (
                      <tr 
                        key={rep.user_id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            {index === 0 && performance.length > 1 && rep.conversion_rate > 0 && (
                              <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                                Top
                              </Badge>
                            )}
                            <div>
                              <p className="font-medium text-slate-900">{rep.name}</p>
                              <p className="text-xs text-slate-500 capitalize">
                                {rep.role?.replace("_", " ")}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="font-medium text-slate-900">{rep.total_leads}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="font-medium text-emerald-600">{rep.closed_won}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="font-medium text-red-600">{rep.closed_lost}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <Badge 
                            className={
                              rep.conversion_rate >= 50 
                                ? "bg-emerald-50 text-emerald-700" 
                                : rep.conversion_rate >= 25 
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-slate-100 text-slate-600"
                            }
                          >
                            {rep.conversion_rate}%
                          </Badge>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="text-slate-600">{rep.total_calls}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="text-slate-600">{rep.calls_this_week}</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insights */}
      {performance.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Top Performer
                </h4>
                {performance.filter(r => r.conversion_rate > 0).length > 0 ? (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="font-medium text-slate-900">
                      {performance.sort((a, b) => b.conversion_rate - a.conversion_rate)[0]?.name}
                    </p>
                    <p className="text-sm text-emerald-700 mt-1">
                      {performance.sort((a, b) => b.conversion_rate - a.conversion_rate)[0]?.conversion_rate}% conversion rate
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No closed deals yet</p>
                )}
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Most Active This Week
                </h4>
                {performance.filter(r => r.calls_this_week > 0).length > 0 ? (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="font-medium text-slate-900">
                      {performance.sort((a, b) => b.calls_this_week - a.calls_this_week)[0]?.name}
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      {performance.sort((a, b) => b.calls_this_week - a.calls_this_week)[0]?.calls_this_week} calls this week
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No calls logged this week</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
